import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import type { GetStaticProps } from 'next';
import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryTwoForOnePageBySlug, queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery, PageBySlugQuery } from 'types/generated';
import { Label } from 'components/src/typography';
import Ticket from '@/components/Adstod/Ticket';
import Categories from '@/components/Adstod/Categories';
import Image from 'next/image';
import PageContainer from '@/components/PageContainer';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';
import LoadingIcon from '@/components/Icons/LoadingIcon';
import AdstodImage from '@/components/Icons/1x1-adstod@2x-50.jpg';
import { Log } from '@/logging/datadog';

type Props = {
  query: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const AdstodPage = ({ query, navbar, footer, radioSchedules }: Props) => {
  const [data, setData] = useState<[]>([]);

  interface ItemType {
    // Define the structure of the details object here
    articleNumber: string;
    categoryGroups: {
      groupLabel: string;
      groupName: string;
      selectedCategories: {
        categoryLabel: string;
        categoryName: string;
        url: string;
      }[];
    }[];
    downVoteCount: number;
    id: string;
    lastPublishedDate: string;
    nextReviewDate: string | null;
    summary: string;
    title: string;
    upVoteCount: number;
    url: string;
    urlName: string;
    viewCount: number;
    viewScore: number;
    // Add more fields as needed
  }

  const router = useRouter();
  const { id } = router.query;

  const articleRefs = useRef<{ [key: string]: HTMLLIElement | undefined }>({});

  useEffect(() => {
    if (typeof id === 'string' && articleRefs.current[id as keyof typeof articleRefs.current]) {
      articleRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [id, data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const credentials = process.env.NEXT_PUBLIC_SPURT_SVARAD_CREDENTIALS;
        const response = await fetch(`${process.env.NEXT_PUBLIC_SPURT_SVARAD_ENDPOINT}Algengar_spurningar%22%7D`, {
          headers: {
            Authorization: `Basic ${credentials}`,
          },
        });
        const jsonData = await response.json();
        // Ensure jsonData is an array or transform it into one before setting it
        setData(Array.isArray(jsonData.articles) ? jsonData.articles : []);
      } catch (error) {
        Log.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const pageCollection = query.pageCollection?.items[0];

  const ticketShouldOpen = (ticketId: string) => {
    if (id) {
      if (id === ticketId) {
        return true;
      }
    }
    return false;
  };

  if (!pageCollection) {
    return (
      <div>
        <Label>{'404 /2fyrir1 page not found'}</Label>
      </div>
    );
  }

  return (
    <PageContainer
      title="Sýn | Aðstoð"
      metaTags={{
        title: 'Sýn Aðstoð',
        url: 'https://www.syn.is/adstod',
        keywords: 'syn,adstod',
        image: { url: '/android-chrome-192x192.png' },
      }}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <div
        className="col-span-full mt-64"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            marginBottom: '50px',
            marginTop: '128px',
            maxWidth: '1016px',
            maxHeight: '496px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          className="justify-space-between mx-16 flex-col items-center gap-16 lg:mx-0 lg:flex-row"
        >
          <div className="mt-4 flex flex-col sm:mt-0">
            <h1
              style={{
                fontSize: '56px',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: 'normal',
                color: '#4A4D4E',
                marginBottom: '1rem',
              }}
            >
              Getum við aðstoðað?
            </h1>
            <p>
              Hér finnur þú svör við algengum spurningum, við erum svo til þjónustu reiðubúin á netspjallinu og í 1414.{' '}
              <a style={{ color: '#F14157' }} href="https://syn.is/afgreidslutimar#thjonustuver-simi">
                Sjá afgreiðslutíma
              </a>
            </p>
          </div>
          <div style={{ width: '50px' }}></div>
          <div style={{ maxWidth: '80%' }}>
            <Image className="rounded-lg" width={350} height={350} src={AdstodImage} alt="spurt og svarað mynd" />
          </div>
        </div>
        <div style={{ marginBottom: '100px', marginTop: '100px', maxWidth: '1016px', width: '80%' }}>
          <Categories />
        </div>
        <div style={{ width: '100%', backgroundColor: 'white', display: 'flex', justifyContent: 'center' }}>
          <div style={{ marginTop: '50px', marginBottom: '50px', width: '808px' }} className="mx-16 lg:mx-0">
            <h2
              style={{
                fontSize: '32px',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: 'normal',
                color: '#4A4D4E',
              }}
            >
              Algengar spurningar
            </h2>
            {data.length > 0 ? (
              <ul>
                {data.map((item: ItemType) => (
                  <li
                    key={item.id}
                    ref={(el) => {
                      if (el) articleRefs.current[item.id] = el;
                    }}
                  >
                    <Ticket ticketInfo={item} ticketOpenURL={ticketShouldOpen(item.id)}></Ticket>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-center justify-center">
                <LoadingIcon />
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const slug = '2fyrir1';
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const contentfulPage = await queryTwoForOnePageBySlug(apolloClient, slug);
    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    return {
      props: {
        navbar: navbar.data,
        query: contentfulPage.data,
        footer: footer.data,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default AdstodPage;
